const AUTH_PATHS = [
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/verify-email",
];

const ACTION_SEGMENTS = new Set([
  "new",
  "edit",
  "book",
  "preview",
  "voucher",
  "print",
]);

const RELATED_ENTRY_PREFIXES = {
  "/payment-proof": ["/wallet-credit", "/bookings"],
  "/settings": ["/dashboard", "/agency-detail"],
  "/agency-detail": ["/dashboard", "/settings"],
};

export function normalizePath(path) {
  if (!path || typeof path !== "string") return "";
  const [pathname] = path.split("?");
  const cleaned = pathname.trim().replace(/\/+$/, "");
  return cleaned || "/";
}

export function splitPathAndSearch(path) {
  if (!path || typeof path !== "string") return { pathname: "", search: "" };
  const [pathname, search = ""] = path.split("?");
  return {
    pathname: normalizePath(pathname),
    search: search ? `?${search}` : "",
  };
}

function isAuthPath(pathname) {
  const path = normalizePath(pathname);
  return AUTH_PATHS.some((authPath) => path === authPath || path.startsWith(`${authPath}/`));
}

export function isSafeInternalPath(path) {
  const { pathname } = splitPathAndSearch(path);
  if (!pathname.startsWith("/")) return false;
  if (pathname.includes("//") || pathname.includes("\\")) return false;
  if (isAuthPath(pathname)) return false;
  return true;
}

export function getParentPath(pathname) {
  const path = normalizePath(pathname);
  if (!path || path === "/" || path === "/dashboard") return "/dashboard";

  const segments = path.split("/").filter(Boolean);
  if (segments.length <= 1) return "/dashboard";

  const last = String(segments[segments.length - 1] || "").toLowerCase();
  if (ACTION_SEGMENTS.has(last)) {
    const parent = `/${segments.slice(0, -1).join("/")}`;
    return parent || "/dashboard";
  }

  return `/${segments.slice(0, -1).join("/")}` || "/dashboard";
}

function pathEquals(left, right) {
  return normalizePath(left) === normalizePath(right);
}

function matchesPrefix(pathname, prefix) {
  const path = normalizePath(pathname);
  const root = normalizePath(prefix);
  return path === root || path.startsWith(`${root}/`);
}

function isRelatedPreviousPath(currentPath, candidatePath, parentPath) {
  const candidate = normalizePath(candidatePath);
  const current = normalizePath(currentPath);
  if (!candidate || candidate === current) return false;
  if (pathEquals(candidate, parentPath)) return true;

  const relatedPrefixes = RELATED_ENTRY_PREFIXES[current] || [];
  return relatedPrefixes.some((prefix) => matchesPrefix(candidate, prefix));
}

export function resolveBackTo({
  pathname,
  fallbackTo,
  from,
  strictParent = false,
} = {}) {
  const currentPath = normalizePath(pathname) || "/dashboard";
  const parentPath = normalizePath(fallbackTo) || getParentPath(currentPath) || "/dashboard";

  if (from && isSafeInternalPath(from)) {
    const { pathname: fromPath, search } = splitPathAndSearch(from);
    if (pathEquals(fromPath, parentPath) || (!strictParent && isRelatedPreviousPath(currentPath, fromPath, parentPath))) {
      return `${fromPath}${search}`;
    }
  }

  return parentPath || "/dashboard";
}
